import toastr from 'toastr';
/** import third-party libraries in the section above, Sort Lines Ascending */

import {
    getApiErrorMessage,
    getDefaultToastTitle,
    resolveHttpError,
} from 'utils/http/get-api-error-message';
/** import single application imports in the section above, Sort Lines Ascending */

const THIRTY_SECONDS = 30000;

const NETWORK_UNAVAILABLE_MESSAGE =
    'Unable to reach the server. Please check your connection and try again.';

const GRAPHQL_GENERIC_MESSAGE = 'Please refresh the page and try again.';

/** Body when HTTP status is >= 500 and Urban body has no usable user-facing text. */
const SERVER_ERROR_TOAST_MESSAGE =
    'Our servers were unable to process your request. Please try again later.';

/** Body when there is no numeric HTTP status (empty / VError noise). Usually connectivity. */
const UNKNOWN_ERROR_TOAST_MESSAGE = 'Please check your connection and try again.';

/**
 * @param {unknown} error
 * @returns {boolean}
 */
function hasGraphqlErrors(error) {
    if (!error || typeof error !== 'object') {
        return false;
    }

    if (Array.isArray(error.response?.errors)) {
        return true;
    }

    return (
        error.body !== null &&
        error.body !== undefined &&
        typeof error.body === 'object' &&
        !Array.isArray(error.body) &&
        Array.isArray(error.body.errors)
    );
}

/**
 * True for empty or VError wrapper noise that must not be shown as toast body.
 *
 * @param {string} trimmed
 * @returns {boolean}
 */
function isNoisyErrorMessage(trimmed) {
    return trimmed === '' || trimmed === 'error' || trimmed === 'error:';
}

/**
 * Fallback for `getApiErrorMessage` when the error body has no user-facing text.
 *
 * GraphQL errors (`response.errors` or `body.errors`) return actionable body text — not
 * technical validation text (title carries the failure label). Network failures return a
 * connection message. Empty / VError noise uses status-aware copy: 5xx gets a server body;
 * unknown (no status) assumes connectivity is the usual cause and does not repeat the title.
 *
 * @param {unknown} error
 * @returns {string}
 */
export function getToastFallback(error) {
    if (!error || typeof error !== 'object') {
        return UNKNOWN_ERROR_TOAST_MESSAGE;
    }

    // Checked before network — GraphQL ClientError must not surface dev-noise error.message.
    if (hasGraphqlErrors(error)) {
        return GRAPHQL_GENERIC_MESSAGE;
    }

    const message = typeof error.message === 'string' ? error.message : '';

    if (
        error.code === 'ECONNREFUSED' ||
        error.code === 'ENOTFOUND' ||
        error.code === 'ETIMEDOUT' ||
        message.includes('ECONNREFUSED') ||
        message.includes('NS_ERROR_CONNECTION_REFUSED') ||
        message.includes('Failed to fetch') ||
        message.includes('NetworkError')
    ) {
        return NETWORK_UNAVAILABLE_MESSAGE;
    }

    const trimmed = message.trim();

    // Empty / VError noise — 5xx server body vs unknown connectivity cue (not a title echo).
    if (isNoisyErrorMessage(trimmed)) {
        // Prefer status from createHttp reject (may be nested under VError).
        const resolvedCode = resolveHttpError(error)?.code;
        const isServerError = typeof resolvedCode === 'number' && resolvedCode >= 500;

        return isServerError ? SERVER_ERROR_TOAST_MESSAGE : UNKNOWN_ERROR_TOAST_MESSAGE;
    }

    return trimmed;
}

/** Default toastr behavior: dismiss after 30s, tap to dismiss, bottom-left. */
export const DEFAULT_TOASTR_OPTIONS = {
    tapToDismiss: true,
    showDuration: 1000,
    showMethod: 'slideDown',
    timeOut: THIRTY_SECONDS,
    extendedTimeOut: 0,
    positionClass: 'toast-bottom-left',
    closeButton: true,
    escapeHtml: true,
};

/**
 * Clear existing toasts, then show API error message in a single toastr (apps-and-sites-list, creative-form).
 * Urban 4xx responses may be a JSON array of `{ field, message }` on `error.body`.
 * GraphQL and network failures use user-facing fallbacks from `getToastFallback`.
 *
 * @param {{ body?: unknown, bodyText?: string, code?: number, message?: string, response?: { errors?: unknown[] } }} error
 * @param {object} [options]
 */
export function createServerErrorAsToastr(error, options = {}) {
    toastr.clear();

    const toastType = options.toastType || 'warning';
    const toast = toastr[toastType] || toastr.error;
    const defaultTitle = getDefaultToastTitle(error);
    const title =
        options.title !== null && options.title !== undefined ? options.title : defaultTitle;
    const toastrOptions = {
        ...DEFAULT_TOASTR_OPTIONS,
        ...options.toastrOptions,
    };

    const toastFallback = getToastFallback(error);

    // One toast with joined messages (replaces per-item loop over error.body).
    const message = getApiErrorMessage(error, toastFallback);
    if (message) {
        toast(message, title, toastrOptions);
        return;
    }

    if (options.fallbackMessage) {
        const fallbackTitle =
            options.fallbackTitle !== null && options.fallbackTitle !== undefined
                ? options.fallbackTitle
                : title;
        toast(options.fallbackMessage, fallbackTitle, toastrOptions);
    }
}
